import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEmployee } from 'context/EmployeeContext';
import { getMyBenefits, getMyCompanyBenefitPlans } from 'services/employeePortal';

const TYPE_COLORS = {
    Medical: { bg: '#e3f2fd', text: '#1565c0' },
    Dental:  { bg: '#e8f5e9', text: '#2e7d32' },
    Vision:  { bg: '#f3e5f5', text: '#6a1b9a' },
    '401k':  { bg: '#fff3e0', text: '#e65100' },
    HSA:     { bg: '#e0f7fa', text: '#00695c' },
    FSA:     { bg: '#fce4ec', text: '#880e4f' },
    LifeInsurance: { bg: '#ede7f6', text: '#4527a0' },
    Disability:    { bg: '#f3e5f5', text: '#6a1b9a' },
    Other:         { bg: '#f5f5f5', text: '#424242' },
};

function fmt(val, fallback = '—') {
    return val != null && val !== '' ? val : fallback;
}

function fmtContrib(amount, percent) {
    if (percent > 0) return `${percent}%`;
    if (amount > 0) return `$${parseFloat(amount).toFixed(2)}`;
    return '—';
}

function StatusBadge({ active, pendingReview }) {
    if (pendingReview) return (
        <span style={{ background: '#fff3cd', color: '#856404', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, border: '1px solid #ffc107' }}>
            Pending Review
        </span>
    );
    if (active === false) return (
        <span style={{ background: '#f8d7da', color: '#721c24', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>
            Inactive
        </span>
    );
    return (
        <span style={{ background: '#d4edda', color: '#155724', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>
            Active
        </span>
    );
}

function BenefitCard({ benefit, planMap }) {
    const plan = planMap[benefit.companyBenefitId];
    const type = benefit.type || plan?.type || 'Other';
    const planName = benefit.description || plan?.description || type;
    const colors = TYPE_COLORS[type] || TYPE_COLORS.Other;
    const period = benefit.period || plan?.period;
    const preTax = plan?.preTax ?? benefit.preTax;
    const waitingPeriod = plan?.waitingPeriod ?? benefit.waitingPeriod;
    const planDocuments = (() => {
        const DOC_LABELS = { SBC: 'SBC', PlanDoc: 'Plan Doc', EnrollmentGuide: 'Enrollment Guide', NetworkDirectory: 'Network Dir', Formulary: 'Formulary', Other: 'Other' };
        try {
            if (plan?.documents) {
                const parsed = JSON.parse(plan.documents);
                if (Array.isArray(parsed) && parsed.length > 0) return { docs: parsed, labels: DOC_LABELS };
            }
        } catch { /* ignore */ }
        if (plan?.documentUrl) return { docs: [{ type: 'PlanDoc', url: plan.documentUrl, name: 'View Plan Document' }], labels: DOC_LABELS };
        return null;
    })();

    const WAITING_LABELS = {
        Immediate: 'Immediate',
        FirstOfMonthAfterHire: '1st of month after hire',
        '30days': '30 days',
        '60days': '60 days',
        '90days': '90 days',
    };

    const employerContrib = fmtContrib(
        benefit.companyContribution ?? plan?.companyContributionAmount,
        benefit.companyContributionPercent ?? plan?.companyContributionPercent
    );
    // If percent-based and totalPremiumAmount is set, show the calculated dollar amount
    const empPct = benefit.employeeContributionPercent;
    const premiumAmt = plan?.totalPremiumAmount ?? benefit.totalPremiumAmount;
    const employeeContrib = (empPct > 0 && premiumAmt > 0)
        ? `$${(parseFloat(premiumAmt) * parseFloat(empPct) / 100).toFixed(2)}/period`
        : fmtContrib(benefit.employeeContributionAmount ?? benefit.employeeDeduction, empPct);
    const effectiveDate = benefit.effectiveStart
        ? new Date(benefit.effectiveStart).toLocaleDateString('en-US', { timeZone: 'UTC' })
        : '—';

    return (
        <div style={{
            background: '#fff',
            border: `1.5px solid ${benefit.pendingReview ? '#ffc107' : '#e5e7eb'}`,
            borderRadius: 12,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                        background: colors.bg, color: colors.text,
                        fontWeight: 700, fontSize: 13, padding: '4px 14px', borderRadius: 20,
                    }}>
                        {type}
                    </span>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e' }}>{planName}</div>
                        {period && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{period} contributions</div>}
                    </div>
                </div>
                <StatusBadge active={benefit.active} pendingReview={benefit.pendingReview} />
            </div>

            {/* Contribution breakdown */}
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 12, background: '#f9fafb', borderRadius: 8, padding: '12px 16px',
            }}>
                <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Employer Pays
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: '#2e7d32', marginTop: 2 }}>
                        {employerContrib}
                    </div>
                    {benefit.pendingReview && employerContrib === '—' && (
                        <div style={{ fontSize: 11, color: '#E7730A', marginTop: 2 }}>Being configured</div>
                    )}
                </div>
                <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {(type === 'STD' || type === 'LTD') ? 'Coverage' : 'Your Deduction'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: (type === 'STD' || type === 'LTD') ? '#1d4ed8' : '#c62828', marginTop: 2 }}>
                        {employeeContrib}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Effective Date
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginTop: 2 }}>
                        {effectiveDate}
                    </div>
                </div>
                {preTax != null && (
                    <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Tax Treatment
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, color: preTax ? '#2e7d32' : '#374151' }}>
                            {preTax ? 'Pre-tax' : 'Post-tax'}
                        </div>
                    </div>
                )}
                {waitingPeriod && (
                    <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Waiting Period
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginTop: 2 }}>
                            {WAITING_LABELS[waitingPeriod] || waitingPeriod}
                        </div>
                    </div>
                )}
            </div>

            {/* Plan documents */}
            {planDocuments && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {planDocuments.docs.map((doc, i) => (
                        <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                fontSize: 13, color: '#E7730A', fontWeight: 600, textDecoration: 'none',
                                padding: '5px 12px', border: '1.5px solid #E7730A', borderRadius: 6,
                            }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                            {planDocuments.labels[doc.type] || doc.type || 'Plan Document'}
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

const EmployeeBenefits = () => {
    const { employeeId } = useEmployee();
    const { data: myBenefits = [], isLoading: loadingBenefits } = useQuery({
        queryKey: ['ep-my-benefits', employeeId],
        queryFn: () => getMyBenefits(employeeId),
        enabled: !!employeeId,
    });

    const { data: companyPlans = [], isLoading: loadingPlans } = useQuery({
        queryKey: ['ep-company-plans'],
        queryFn: getMyCompanyBenefitPlans,
    });


    const planMap = useMemo(() => {
        const map = {};
        companyPlans.forEach(p => { map[p.id] = p; });
        return map;
    }, [companyPlans]);

    const activeEnrollments = myBenefits.filter(b => b.active !== false);
    const inactiveEnrollments = myBenefits.filter(b => b.active === false);

    const enrolledPlanIdSet = new Set(activeEnrollments.map(b => b.companyBenefitId).filter(Boolean));
    const enrolledTypeSet   = new Set(activeEnrollments.map(b => b.type || planMap[b.companyBenefitId]?.type));
    // Show all active plans not specifically enrolled in.
    // If multiple plans exist for a type (e.g. 2 Medical plans), show the unenrolled ones
    // as alternatives the employee can ask HR to switch to.
    const availablePlans = companyPlans.filter(p => {
        if (p.active === false) return false;
        if (enrolledPlanIdSet.has(p.id)) return false; // already enrolled in this exact plan
        // If only one plan of this type exists and they're enrolled in that type, hide it
        const plansOfType = companyPlans.filter(cp => cp.type === p.type && cp.active !== false);
        if (plansOfType.length === 1 && enrolledTypeSet.has(p.type)) return false;
        return true;
    });

    const isLoading = loadingBenefits || loadingPlans;

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>My Benefits</h4>
            <p style={{ color: '#6c757d', marginBottom: 28, fontSize: 14 }}>
                Your current benefit enrollments and employer contributions. Enrollment changes are only permitted during open enrollment periods or qualifying life events — contact HR for assistance.
            </p>

            {isLoading && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                    Loading your benefits...
                </div>
            )}

            {!isLoading && activeEnrollments.length === 0 && (
                <div style={{
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                    padding: '32px', textAlign: 'center', marginBottom: 28,
                }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>🏥</div>
                    <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>No active benefit enrollments</div>
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>
                        If you elected benefits during onboarding, they may be pending employer review.
                    </div>
                </div>
            )}

            {/* Active enrollments */}
            {!isLoading && activeEnrollments.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 14, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Active Enrollments
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: 16 }}>
                        {activeEnrollments.map(b => (
                            <BenefitCard key={b.id} benefit={b} planMap={planMap} />
                        ))}
                    </div>
                </div>
            )}

            {/* Available plans not yet enrolled */}
            {!isLoading && availablePlans.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <h6 style={{ fontWeight: 700, color: '#374151', marginBottom: 8, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Available Plans
                    </h6>
                    <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 14 }}>
                        Your employer offers these additional plans. Enrollment changes are handled during open enrollment periods or qualifying life events. Contact HR to learn more.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: 12 }}>
                        {availablePlans.map(plan => {
                            const colors = TYPE_COLORS[plan.type] || TYPE_COLORS.Other;
                            const isAlternative = enrolledTypeSet.has(plan.type);
                            return (
                                <div key={plan.id} style={{
                                    background: '#fff',
                                    border: `1px solid ${isAlternative ? '#fde68a' : '#e5e7eb'}`,
                                    borderRadius: 10, padding: '16px 20px',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                                        <span style={{ background: colors.bg, color: colors.text, fontWeight: 700, fontSize: 12, padding: '3px 12px', borderRadius: 20 }}>
                                            {plan.type}
                                        </span>
                                        <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{plan.description}</span>
                                        {isAlternative && (
                                            <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 10 }}>
                                                Alternative Plan
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {(plan.companyContributionAmount > 0 || plan.companyContributionPercent > 0) && (
                                            <span>Employer contribution: <strong style={{ color: '#2e7d32' }}>
                                                {fmtContrib(plan.companyContributionAmount, plan.companyContributionPercent)}
                                            </strong></span>
                                        )}
                                        {(plan.employeeContributionAmount > 0 || plan.employeeContributionPercent > 0) && (
                                            <span>Employee cost: <strong>
                                                {(plan.employeeContributionPercent > 0 && plan.totalPremiumAmount > 0)
                                                    ? `$${(parseFloat(plan.totalPremiumAmount) * parseFloat(plan.employeeContributionPercent) / 100).toFixed(2)}/period`
                                                    : fmtContrib(plan.employeeContributionAmount, plan.employeeContributionPercent)}
                                            </strong></span>
                                        )}
                                        {plan.period && <span>Period: {plan.period}</span>}
                                    </div>
                                    {(() => {
                                        const DOC_LABELS = { SBC: 'SBC', PlanDoc: 'Plan Doc', EnrollmentGuide: 'Enrollment Guide', NetworkDirectory: 'Network Dir', Formulary: 'Formulary', Other: 'Other' };
                                        let docs = [];
                                        try { if (plan.documents) docs = JSON.parse(plan.documents); } catch { /* ignore */ }
                                        if (docs.length === 0 && plan.documentUrl) docs = [{ type: 'PlanDoc', url: plan.documentUrl, name: 'View Plan Document' }];
                                        if (docs.length === 0) return null;
                                        return (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                                {docs.map((doc, i) => (
                                                    <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer"
                                                        style={{ fontSize: 12, color: '#E7730A', fontWeight: 600, textDecoration: 'none' }}>
                                                        📄 {DOC_LABELS[doc.type] || doc.type || 'Plan Document'}
                                                    </a>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Inactive/past enrollments */}
            {!isLoading && inactiveEnrollments.length > 0 && (
                <div>
                    <h6 style={{ fontWeight: 700, color: '#9ca3af', marginBottom: 14, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Inactive Enrollments
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: 16 }}>
                        {inactiveEnrollments.map(b => (
                            <BenefitCard key={b.id} benefit={b} planMap={planMap} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeBenefits;
